import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import NumberRenderer from 'components/common/ag-grid/NumberRenderer';
import JobFormatRenderer from 'components/app/crm/reports/components/JobFormatRenderer';
import DateFormatRenderer from 'components/app/crm/reports/components/DateFormatRenderer';
import ShopFormatRenderer from 'components/app/crm/reports/components/ShopFormatRenderer';
import APDFormatRenderer from 'components/app/crm/reports/components/APDFormatRenderer';
import StatusBuildingFormatRenderer from './StatusFormatRenderer';

// Keys match the backend co_status enum (canonical snake_case values).
export const CO_STATUS_MAP = {
  submitted:           { bg: '#f1f5f9', border: '#94a3b8', text: '#475569', label: 'Submitted'           },
  sent:                { bg: '#e0f2fe', border: '#38bdf8', text: '#0369a1', label: 'Sent to CSD'         },
  requested_revise:    { bg: '#fff7ed', border: '#fb923c', text: '#c2410c', label: 'Requested Revise'    },
  accepted_revise:     { bg: '#fffbeb', border: '#fbbf24', text: '#b45309', label: 'Accepted Revise'     },
  accepted:            { bg: '#dcfce7', border: '#4ade80', text: '#15803d', label: 'Accepted'            },
  rejected:            { bg: '#fee2e2', border: '#f87171', text: '#b91c1c', label: 'Rejected'            },
  rejected_and_closed: { bg: '#e2e8f0', border: '#64748b', text: '#334155', label: 'Rejected and Closed' },
};

export const CoStatusBadge = ({ value }) => {
  if (!value) return <span className="tw-text-slate-300">—</span>;
  const s = CO_STATUS_MAP[value] ?? { bg: '#eff6ff', border: '#93c5fd', text: '#1d4ed8', label: String(value).replace(/_/g, ' ') };
  return (
    <span
      style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.text }}
      className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-md tw-text-[11px] tw-font-semibold tw-leading-4"
    >
      {s.label}
    </span>
  );
};
CoStatusBadge.propTypes = { value: PropTypes.string };

// Only set when the CO#'s LATEST REV# is waiting for CSD to accept/reject —
// the backend decides that (Crm::Reports::ChangeOrder#pending_revision).
export const PendingRevisionBadge = ({ value }) => {
  if (!value) return <span className="tw-text-slate-300">—</span>;
  const since = value.sent_at ? dayjs(value.sent_at).format('DD-MMM-YYYY') : null;
  return (
    <span
      title={since ? `Waiting CSD accept/reject since ${since}` : 'Waiting CSD accept/reject'}
      className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-md tw-border tw-border-solid tw-border-amber-300 tw-bg-amber-50 tw-text-amber-700 tw-text-[11px] tw-font-semibold tw-leading-4"
    >
      <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-amber-500" />
      {value.name}
    </span>
  );
};
PendingRevisionBadge.propTypes = {
  value: PropTypes.shape({ name: PropTypes.string, sent_at: PropTypes.string }),
};

// Opens the CO# detail page; null when the row lacks any of the three ids.
export const changeOrderPath = (row) => {
  const projectId = row?.project?.id;
  const buildingId = row?.building?.id;
  if (!projectId || !buildingId || !row?.id) return null;
  return `/crm/projects/${projectId}/buildings/${buildingId}/change-orders/${row.id}`;
};

// Sorting round-trips to the server, where `sort.attribute` is fed straight to
// Ransack. Ransack silently ignores an attribute it does not recognise, so a
// column whose colId is not a real Ransack key would show a sort arrow and do
// nothing. Two rules therefore hold throughout this file:
//   1. every sortable column's colId IS its Ransack sort key;
//   2. anything Ransack cannot order — multi-value phase dates, Postgres array
//      columns, values that live on a joined row — is marked sortable: false.
const NOT_SORTABLE = { sortable: false };

// Long free text: AG Grid truncates, the tooltip gives the whole value back.
const TEXT_TOOLTIP = { tooltipValueGetter: (p) => p.value };

export const COLUMN_DEFS = [
  {
    headerName: ' ',
    children: [
      {
        headerName:             'Quote Number',
        colId:                  'project_q_number',
        minWidth:               100,
        width: 120,
        flex: 0,
        pinned:                 'left',
        valueGetter:            (p) => p.data?.project?.q_number,
        cellRenderer:           (p) => {
          const proj = p.data?.project;
          if (!proj) return null;
          return <Link to={`/crm/projects/${proj.id}`} className="tw-text-yellow-600 hover:tw-underline tw-font-medium">{proj.q_number}</Link>;
        },
      },
      {
        headerName:   'P/J Number',
        colId:        'building_j_number',
        minWidth:     120,
        width:        120,
        flex:         0,
        pinned:       'left',
        valueGetter:  (p) => p.data?.building?.j_number,
        cellRenderer: JobFormatRenderer,
      },
      {
        headerName:   'Project Name',
        colId:        'project_name',
        minWidth:     200,
        ...TEXT_TOOLTIP,
        valueGetter:  (p) => p.data?.project?.name,
        cellRenderer: (p) => {
          const proj = p.data?.project;
          if (!proj) return null;
          return <Link to={`/crm/projects/${proj.id}`} className="tw-text-blue-600 hover:tw-underline">{proj.name}</Link>;
        },
      },
      { headerName: 'Building',    colId: 'building_name',        minWidth: 120, valueGetter: (p) => p.data?.building?.name        },
      { headerName: 'Description', colId: 'building_description', minWidth: 200, ...TEXT_TOOLTIP, valueGetter: (p) => p.data?.building?.description },
      // Was showing sales_unit.region — the same value as the Region column at
      // the far right, while the project's own location went unshown. The Excel
      // export has always used pj_location for this column.
      { headerName: 'Location',    colId: 'project_pj_location',  minWidth: 130, ...TEXT_TOOLTIP, valueGetter: (p) => p.data?.project?.pj_location },
      {
        headerName:   'CO',
        field:        'co',
        minWidth:     70,
        cellRenderer: (p) => {
          const href = changeOrderPath(p.data);
          if (!href) return p.value;
          return <Link to={href} target="_blank" rel="noopener noreferrer" className="tw-text-blue-600 hover:tw-underline">{p.value}</Link>;
        },
      },
      {
        headerName:   'CO Status',
        field:        'co_status',
        minWidth:     145,
        cellRenderer: (p) => <CoStatusBadge value={p.value} />,
      },
      {
        headerName:   'REV# Pending',
        colId:        'pending_revision',
        minWidth:     120,
        ...NOT_SORTABLE,
        valueGetter:  (p) => p.data?.pending_revision,
        cellRenderer: (p) => <PendingRevisionBadge value={p.value} />,
      },
      {
        headerName:   'Entry Date',
        field:        'entry_date',
        minWidth:     110,
        cellRenderer: DateFormatRenderer,
      },
      {
        // First "Request schedule to ENG" — a re-send keeps this date.
        headerName:   'Entered to ENG',
        field:        'eng_requested_at',
        minWidth:     120,
        cellRenderer: DateFormatRenderer,
        tooltipValueGetter: (p) => p.data?.eng_requested_by && `Requested by ${p.data.eng_requested_by}`,
      },
      {
        headerName:  'Sales In-charge',
        colId:       'project_sales_incharge',
        minWidth:    130,
        valueGetter: (p) => p.data?.project?.sales_incharge,
      },
    ],
  },
  {
    headerName: 'Weight (M.T)',
    children: [
      { headerName: 'JAF',      colId: 'building_jaf_weight',  minWidth: 110, cellRenderer: NumberRenderer, valueGetter: (p) => p.data?.building?.jaf_weight },
      { headerName: 'JAF Note', colId: 'building_jaf_note',    minWidth: 160, ...TEXT_TOOLTIP, valueGetter: (p) => p.data?.building?.jaf_note },
      { headerName: 'EST',      colId: 'est_released_weight',  minWidth: 110, cellRenderer: NumberRenderer, valueGetter: (p) => p.data?.estimate?.weight },
      { headerName: 'BOM',      field: 'bom_weight',           minWidth: 110, cellRenderer: NumberRenderer },
    ],
  },
  {
    headerName: 'EST',
    children: [
      { headerName: 'Request',  colId: 'est_requested_date', minWidth: 110, cellRenderer: DateFormatRenderer, valueGetter: (p) => p.data?.estimate?.request  },
      { headerName: 'Schedule', colId: 'est_scheduled_date', minWidth: 110, cellRenderer: DateFormatRenderer, valueGetter: (p) => p.data?.estimate?.schedule },
      { headerName: 'Release',  colId: 'est_released_date',  minWidth: 110, cellRenderer: DateFormatRenderer, valueGetter: (p) => p.data?.estimate?.release  },
    ],
  },
  // Every phase column below holds one line per part/phase of the change order.
  // There is no single value to order 918 rows by, so they never offer a sort.
  {
    headerName: 'APD - A/B',
    children: [
      { headerName: 'Request',  colId: 'apds.apd_request',  minWidth: 130, ...NOT_SORTABLE, cellRenderer: APDFormatRenderer },
      { headerName: 'Schedule', colId: 'apds.apd_schedule', minWidth: 130, ...NOT_SORTABLE, cellRenderer: APDFormatRenderer },
      { headerName: 'Released', colId: 'apds.apd_release',  minWidth: 130, ...NOT_SORTABLE, cellRenderer: APDFormatRenderer },
    ],
  },
  {
    headerName: 'SHOP DRAWINGS',
    children: [
      { headerName: 'Request',  colId: 'shops.shop_request',  minWidth: 130, ...NOT_SORTABLE, cellRenderer: ShopFormatRenderer },
      { headerName: 'Schedule', colId: 'shops.shop_schedule', minWidth: 130, ...NOT_SORTABLE, cellRenderer: ShopFormatRenderer },
      { headerName: 'Released', colId: 'shops.shop_release',  minWidth: 130, ...NOT_SORTABLE, cellRenderer: ShopFormatRenderer },
    ],
  },
  {
    headerName: 'A/B for ERD',
    children: [
      { headerName: 'Request',  colId: 'apds.ab_erd_request',  minWidth: 130, ...NOT_SORTABLE, cellRenderer: APDFormatRenderer },
      { headerName: 'Schedule', colId: 'apds.ab_erd_schedule', minWidth: 130, ...NOT_SORTABLE, cellRenderer: APDFormatRenderer },
      { headerName: 'Released', colId: 'apds.ab_erd_release',  minWidth: 130, ...NOT_SORTABLE, cellRenderer: APDFormatRenderer },
    ],
  },
  {
    headerName: 'ERECTION DRAWINGS',
    children: [
      { headerName: 'Request',  colId: 'shops.erc_request',  minWidth: 130, ...NOT_SORTABLE, cellRenderer: ShopFormatRenderer },
      { headerName: 'Schedule', colId: 'shops.erc_schedule', minWidth: 130, ...NOT_SORTABLE, cellRenderer: ShopFormatRenderer },
      { headerName: 'Released', colId: 'shops.erc_release',  minWidth: 130, ...NOT_SORTABLE, cellRenderer: ShopFormatRenderer },
    ],
  },
  {
    headerName: '',
    children: [
      // cse_order_entry / cse_order_management are Postgres text[] columns and
      // building status lives on a joined table — none of them orders sensibly
      // through Ransack, so none offers a sort.
      { headerName: 'CSE OE',          colId: 'building_cse_order_entry',      minWidth: 120, ...NOT_SORTABLE, valueGetter: (p) => p.data?.building?.cse_order_entry },
      { headerName: 'CSE OM',          colId: 'building_cse_order_management', minWidth: 120, ...NOT_SORTABLE, valueGetter: (p) => p.data?.building?.cse_order_management },
      { headerName: 'Building Status', colId: 'building_status',               minWidth: 180, ...NOT_SORTABLE, cellRenderer: StatusBuildingFormatRenderer, valueGetter: (p) => p.data?.building?.status_name },
      { headerName: 'Sales Unit',      colId: 'sales_unit_description',        minWidth: 130, ...NOT_SORTABLE, valueGetter: (p) => p.data?.sales_unit?.description },
      { headerName: 'Region',          colId: 'sales_unit_region',             minWidth: 120, ...NOT_SORTABLE, valueGetter: (p) => p.data?.sales_unit?.region },
      { headerName: 'Created By',      colId: 'user_name',                     minWidth: 130, valueGetter: (p) => p.data?.created_by },
      // The serializer already renders this as "Sep, 04 2026 10:30 AM"; running
      // it through DateFormatRenderer (which expects YYYY-MM-DD) chopped it to
      // "Sep, 04 20".
      { headerName: 'Created Date',    field: 'created_date',                  minWidth: 150 },
    ],
  },
];
